import { useSelectedAccount } from '@features/accounts';
import { useAuthentication } from '@features/authentication/hooks';
import { useSelectedProduction } from '@features/productions';
import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useMemo } from 'react';

import { GetFavoritesArgs } from '../types';
import { useGetFavoritesQuery, useToggleFavoriteMutation } from '../api';

export const useCollectionFavorites = () => {
    const { user } = useAuthentication();
    const account = useSelectedAccount();
    const production = useSelectedProduction();

    const contextArgs = useMemo(() => {
        if (!user?.id || !account?.id || !production?.id) return skipToken;

        return {
            userId: user.id,
            accountId: account.id,
            productionId: production.id,
        } as GetFavoritesArgs;
    }, [user?.id, account?.id, production?.id]);

    const { data: favorites = [], isLoading } =
        useGetFavoritesQuery(contextArgs);
    const [executeToggle, mutationState] = useToggleFavoriteMutation();

    const toggleFavorite = useCallback(
        (collectionId: string, isFavorite: boolean) => {
            if (contextArgs === skipToken) return;
            executeToggle({ ...contextArgs, collectionId, isFavorite });
        },
        [contextArgs, executeToggle],
    );

    const checkIsFavorite = useCallback(
        (collectionId: string) => favorites.includes(collectionId),
        [favorites],
    );

    return {
        favorites,
        isLoading,
        isToggling: mutationState.isLoading,
        toggleFavorite,
        checkIsFavorite,
    };
};
